import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  QueryOperator,
  ExactMatchQueryOperators,
  Filters,
  VqlQuery,
} from "types";

import QueryModalOperators from "views/components/QueryModal/QueryModalOperators";
import QueryModalTitle from "views/components/QueryModal/QueryModalTitle";
import { convertAPIOperator } from "helpers/utility/queryFilterHelpers";
import { getSelectedQueryModal } from "redux/SingleDataset/selectors";
import {
  createAndUpdateIssueTypesFilter,
  isVqlQuery,
} from "helpers/utility/vqlHelpers";
import {
  setSelectedQueryModal,
  updateSelectedQueryModal,
} from "redux/SingleDataset/actions";
import RangeInput from "views/components/QueryModal/RangeInput";
import QueryModal from "views/components/QueryModal";
import useVqlParams from "hooks/useVqlParams";

import MislabelsIcon from "assets/icons/MislabelsIcon";
import styles from "./style.module.scss";

const MISLABELS_OPERATORS: QueryOperator[] = [
  QueryOperator.IS,
  QueryOperator.IS_NOT,
];

interface MislabelsQueryModalProps {
  editQueryId?: string;
}

export default function MislabelsQueryModal({
  editQueryId,
}: MislabelsQueryModalProps) {
  const { findVqlQuery, addVqlQuery, updateVqlQuery, vqlQueries } =
    useVqlParams();
  const selectedModalData = useSelector((state) =>
    getSelectedQueryModal(state),
  );

  const issueQuery = editQueryId ? findVqlQuery(editQueryId, "issues") : null;
  const oldQuery = issueQuery?.issues.value === "mislabels" ? issueQuery : null;

  const queryOperator = oldQuery
    ? convertAPIOperator(oldQuery.issues.mode)
    : QueryOperator.IS;

  const range: [number, number] = oldQuery
    ? [oldQuery.issues.confidence_min, oldQuery.issues.confidence_max]
    : [0.5, 1]; //mock data

  const existingQuery = useMemo(() => {
    if (editQueryId) {
      return null;
    }
    return (
      vqlQueries.find(
        (query: VqlQuery) =>
          isVqlQuery(query, "issues") &&
          query.issues.value === "mislabels" &&
          convertAPIOperator(query.issues?.mode) === queryOperator,
      ) || null
    );
  }, [vqlQueries, queryOperator, editQueryId]);

  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const submitQuery = (operator: QueryOperator, newRange: [number, number]) => {
    const queryId = editQueryId || existingQuery?.id;
    const vqlQuery = createAndUpdateIssueTypesFilter(
      "mislabels",
      operator as ExactMatchQueryOperators,
      newRange,
      queryId,
    );
    queryId ? updateVqlQuery(vqlQuery) : addVqlQuery(vqlQuery);
    if (selectedModalData) {
      dispatch(
        setSelectedQueryModal({ ...selectedModalData, id: vqlQuery.id }),
      );
    }
  };

  const handleOptionSelection = (operator: QueryOperator) => {
    if (operator === queryOperator) return;
    submitQuery(operator, range);
  };

  const handleSubmission = (value: number[]) => {
    if (value[0] === range[0] && value[1] === range[1]) return;

    submitQuery(queryOperator, [value[0], value[1]]);
  };

  useEffect(() => {
    const query = vqlQueries.find(
      (query: VqlQuery) =>
        isVqlQuery(query, "issues") &&
        query.issues?.value === "mislabels" &&
        convertAPIOperator(query.issues?.mode) === queryOperator,
    );
    if (query) {
      dispatch(
        updateSelectedQueryModal({ type: Filters.MISLABELS, id: query.id }),
      );
    } else {
      submitQuery(queryOperator, range);
      dispatch(updateSelectedQueryModal({ type: Filters.MISLABELS }));
    }
    // eslint-disable-next-line
  }, [queryOperator]);

  return (
    <QueryModal modalRef={modalRef} keepOpenOnLoading>
      <QueryModalTitle
        icon={<MislabelsIcon />}
        info="Finds images that were likely given the wrong label. Based on how they look, they fit better in another group, even if label names aren’t meaningful."
      >
        Mislabels
      </QueryModalTitle>
      <div className={styles.filterContent}>
        <QueryModalOperators
          operators={MISLABELS_OPERATORS}
          selectedOperator={queryOperator}
          onSelect={handleOptionSelection}
        />
        <RangeInput
          range={range}
          showActualValues
          title="Confidence:"
          onChange={handleSubmission}
        />
      </div>
    </QueryModal>
  );
}
